import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, App, Button, Card, Col, DatePicker, Descriptions, Empty, Form, Grid, Image, Input, InputNumber, List, Modal,
  Popconfirm, Result, Row, Select, Space, Table, Tag, Tooltip, Typography, Upload, theme,
} from 'antd';
import {
  CameraOutlined, DeleteOutlined, EditOutlined, EyeOutlined, FileExcelOutlined, HomeOutlined, PlusOutlined,
  ReloadOutlined, StopOutlined, UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { enhanceColumns, toplamGenislik } from '../ortak/tableHelpers';
import { aramali } from './formParcalari';
import { HaritaliAdres } from './KonumSecici';
import {
  DurumEtiketi, FOTO_MAKS, FOTO_MAKS_ADET, LokasyonLinki, excelIndir, fotoBuyukUrl, fotoKucult, fotoUrl, hata, para,
  paraGirdisi, pazartesi, tarih,
} from './ortak';

/**
 * S1 Görüşme Kaydı + S2 Lokasyona Dönüştür (26.10.2026) — /satis-sureci/gorusme.
 * Görüşme lokasyon açılmadan ÖNCE tutulur; olumlu olan "Lokasyona Dönüştür" ile maptriks_lokasyonlar'a
 * Aday (durum 1) olur, fotoğrafları poifoto/{MaptriksID}/'ye kopyalanır. Üye yalnız kendi kaydını değiştirir.
 * 27.10: görüşen = kaydı giren (sunucu yazar); satılabilirlik kaldırıldı; il / ilçe / adres haritadan (KonumSecici).
 */
const { Text } = Typography;
const { RangePicker } = DatePicker;

const bosToNull = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v === undefined || v === '' ? null : v]));

function FotoSeridi({ g, boyut = 56, sil, disabled }) {
  const { token } = theme.useToken();
  if (!g.fotograflar?.length) return null;
  return (
    <Image.PreviewGroup>
      <Space size={6} wrap>
        {g.fotograflar.map((ad) => (
          <div key={ad} style={{ position: 'relative' }}>
            <Image width={boyut} height={boyut} style={{ objectFit: 'cover', borderRadius: 6, border: `1px solid ${token.colorBorderSecondary}` }}
              src={fotoUrl(g.id, ad)} preview={{ src: fotoBuyukUrl(g.id, ad) }} alt="Görüşme fotoğrafı" />
            {sil && !disabled && (
              <Popconfirm title="Fotoğraf silinsin mi?" okText="Sil" cancelText="Vazgeç" onConfirm={() => sil(ad)}>
                <Button size="small" danger shape="circle" icon={<DeleteOutlined />} aria-label="Fotoğrafı sil"
                  style={{ position: 'absolute', top: -6, right: -6, width: 20, minWidth: 20, height: 20 }} />
              </Popconfirm>
            )}
          </div>
        ))}
      </Space>
    </Image.PreviewGroup>
  );
}

/** Görüşme formu (yeni / düzenle / görüntüle). Yeni fotoğraflar kayıttan sonra yüklenir. */
function GorusmeFormu({ api, secenek, erisim, kayit, onKapat, onKaydedildi }) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const genis = Grid.useBreakpoint().md;
  const [kayitAni, setKayitAni] = useState(kayit);
  const [yeni, setYeni] = useState([]); // {uid, name, dosya, url}
  // 27.10 düzeltmesi: çoklu seçimde beforeUpload her dosya için AYNI render'ın (eski) yeni.length'iyle
  // çağrılıyordu; 20'yi aşan parti sunucuda toptan 400 alıyordu. Kabul edilen (küçültülmekte olanlar dahil)
  // yeni fotoğraf sayısı ref'te eşzamanlı tutulur.
  const yeniAdet = useRef(0);
  const uyarilanParti = useRef(null);
  const urller = useRef([]); // önizleme URL'leri kapanınca bırakılır
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const sahip = !kayitAni || erisim.yonetici || [kayitAni.gorusen, kayitAni.ekleyen].includes(erisim.kullanici_no);
  const salt = !!kayitAni && (kayitAni.durum === 'donusturuldu' || !sahip || !erisim.yazma);

  useEffect(() => {
    form.setFieldsValue(kayit ? {
      ...kayit, gorusme_tarihi: kayit.gorusme_tarihi ? dayjs(kayit.gorusme_tarihi) : null,
    } : { gorusme_tarihi: dayjs(), olusturma_sebebi: 0 });
  }, [kayit, form]);
  useEffect(() => () => urller.current.forEach((u) => URL.revokeObjectURL(u)), []);

  const fotoEkle = (f, liste) => {
    const toplam = (kayitAni?.fotograflar?.length || 0) + yeniAdet.current;
    if (toplam >= FOTO_MAKS_ADET) {
      if (uyarilanParti.current !== liste) { // aynı seçimdeki (parti) fazla dosyalar için tek uyarı
        uyarilanParti.current = liste;
        message.error(`Bir görüşmede en fazla ${FOTO_MAKS_ADET} fotoğraf olabilir; fazlası eklenmedi.`);
      }
      return Upload.LIST_IGNORE;
    }
    if (f.size > 3 * FOTO_MAKS) {
      message.error(`${f.name}: dosya çok büyük.`);
      return Upload.LIST_IGNORE;
    }
    yeniAdet.current += 1;
    fotoKucult(f).then((k) => {
      if (k.size > FOTO_MAKS) {
        yeniAdet.current -= 1;
        message.error(`${f.name}: küçültülmüş hâli de 10 MB'tan büyük.`);
        return;
      }
      const url = URL.createObjectURL(k);
      urller.current.push(url);
      setYeni((o) => [...o, { uid: f.uid, name: f.name, dosya: k, url }]);
    });
    return Upload.LIST_IGNORE;
  };

  const fotoSil = (ad) => api.del(`/gorusme/${kayitAni.id}/foto/${encodeURIComponent(ad)}`)
    .then((g) => { setKayitAni(g); onKaydedildi(g, true); message.success('Fotoğraf silindi.'); })
    .catch((e) => message.error(hata(e)));

  const kaydet = async () => {
    const v = await form.validateFields();
    const govde = bosToNull({ ...v, gorusme_tarihi: v.gorusme_tarihi.format('YYYY-MM-DD') });
    setKaydediliyor(true);
    try {
      let g = kayitAni ? await api.put(`/gorusme/${kayitAni.id}`, govde) : await api.post('/gorusme', govde);
      if (yeni.length) {
        const fd = new FormData();
        yeni.forEach((f) => fd.append('dosyalar', f.dosya, f.dosya.name));
        try {
          g = await api.form(`/gorusme/${g.id}/foto`, fd);
        } catch (e) {
          message.warning(`Görüşme kaydedildi ama fotoğraflar yüklenemedi: ${hata(e)}`);
        }
      }
      message.success(kayitAni ? 'Görüşme güncellendi.' : 'Görüşme kaydedildi.');
      onKaydedildi(g);
      onKapat();
    } catch (e) {
      message.error(hata(e));
    } finally {
      setKaydediliyor(false);
    }
  };

  return (
    <Modal open title={kayitAni ? `Görüşme #${kayitAni.id}${salt ? ' (salt okunur)' : ''}` : 'Yeni görüşme'}
      onCancel={onKapat} width={genis ? 820 : '100%'} style={genis ? undefined : { top: 0, maxWidth: '100vw', padding: 0 }}
      okText="Kaydet" cancelText={salt ? 'Kapat' : 'Vazgeç'} onOk={kaydet} confirmLoading={kaydediliyor}
      okButtonProps={{ style: salt ? { display: 'none' } : undefined }}>
      {kayitAni?.durum === 'donusturuldu' && (
        <Alert type="success" showIcon style={{ marginBottom: 12 }}
          message={<span>Lokasyona dönüştü: <LokasyonLinki id={kayitAni.lokasyon} /> · {kayitAni.donusturen_adi} · {tarih(kayitAni.donusturme_tarihi)}</span>} />
      )}
      {kayitAni && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>Görüşen: {kayitAni.gorusen_adi || `#${kayitAni.gorusen}`}</Text>
      )}
      <Form form={form} layout="vertical" disabled={salt} requiredMark="optional">
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="gorusme_tarihi" label="Görüşme tarihi" rules={[{ required: true, message: 'Tarih seçin' }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} inputReadOnly
                disabledDate={(d) => d.isAfter(dayjs().add(1, 'day'), 'day')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="olusturma_sebebi" label="Oluşturma sebebi" rules={[{ required: true }]}>
              <Select options={secenek.sebepler} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="lokasyon_adi" label="Lokasyon adı"
          rules={[{ required: true, min: 2, max: 120, whitespace: true, message: 'Lokasyon adı (2–120 karakter)' }]}>
          <Input maxLength={120} placeholder="Ör. Mudanya İstasyon Cd. eczane yanı dükkan" />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="proje" label="Proje">
              <Select {...aramali} options={secenek.projeler} placeholder="Proje (keşif talebi)" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="kategori" label="Kategori">
              <Select {...aramali} options={secenek.kategoriler} placeholder="Lokasyon kategorisi" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Form.Item name="kabin_sayisi" label="Kabin sayısı">
              <InputNumber min={0} max={50} style={{ width: '100%' }} inputMode="numeric" />
            </Form.Item>
          </Col>
          <Col xs={12} sm={9}>
            <Form.Item name="ys_ad" label="Yer sahibi">
              <Input maxLength={250} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={9}>
            <Form.Item name="ys_tel" label="Yer sahibi telefonu">
              <Input maxLength={30} inputMode="tel" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item name="kira_tutar" label="Kira tutarı (₺)">
              <InputNumber {...paraGirdisi} max={1e9} inputMode="decimal" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item name="odeme_sekli" label="Ödeme şekli">
              <Select allowClear options={secenek.odeme_sekilleri} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="kdv_stopaj" label="Vergi">
              <Select allowClear options={secenek.vergi} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="gorusme_notu" label="Görüşme notu">
          <Input.TextArea rows={3} maxLength={5000} showCount
            placeholder="Yer sahibiyle konuşulanlar, bölgenin banka ilgisi, yaya trafiği, rakip ATM'ler…" />
        </Form.Item>
        <HaritaliAdres form={form} api={api} iller={secenek.iller} disabled={salt} />
        <Form.Item label={`Fotoğraflar (${(kayitAni?.fotograflar?.length || 0) + yeni.length}/${FOTO_MAKS_ADET})`}
          extra={salt ? null : 'Yüklemeden önce küçültülür (uzun kenar 1600 px). JPG / PNG / WEBP.'}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {kayitAni && <FotoSeridi g={kayitAni} boyut={72} sil={fotoSil} disabled={salt} />}
            {!salt && (
              <Upload accept="image/*" multiple listType="picture-card" beforeUpload={fotoEkle}
                fileList={yeni.map((f) => ({ uid: f.uid, name: f.name, status: 'done', url: f.url, thumbUrl: f.url }))}
                onRemove={(f) => { yeniAdet.current = Math.max(0, yeniAdet.current - 1); setYeni((o) => o.filter((x) => x.uid !== f.uid)); return true; }}
                showUploadList={{ showPreviewIcon: false }}>
                <div><CameraOutlined style={{ fontSize: 20 }} /><div style={{ marginTop: 4, fontSize: 12 }}>Çek / seç</div></div>
              </Upload>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** 27.10: yakında / aynı adla zaten lokasyon varsa uyarı (PHP 'Lokasyon Ekle' de açık; engellemez). */
function YakinLokasyonlar({ api, g }) {
  const [veri, setVeri] = useState(null);
  useEffect(() => {
    let iptal = false;
    api.get(`/gorusme/${g.id}/yakin-lokasyonlar`).then((d) => { if (!iptal) setVeri(d); }).catch(() => {});
    return () => { iptal = true; };
  }, [api, g.id]);
  if (!veri?.satirlar?.length) return null;
  return (
    <Alert type="warning" showIcon style={{ marginBottom: 12 }}
      message={`Yakında (≤ ${veri.metre} m) ya da aynı ilçede aynı adla ${veri.satirlar.length} lokasyon var — mükerrer olmadığını kontrol edin`}
      description={(
        <ul style={{ margin: 0, paddingLeft: 16 }}>
          {veri.satirlar.map((l) => (
            <li key={l.MaptriksID}>
              <LokasyonLinki id={l.MaptriksID} ad={l.Name} /> · {l.durum_adi || l.durum}
              {l.mesafe_m != null ? ` · ${l.mesafe_m} m` : ''}{l.ayni_ad ? ' · aynı ad' : ''}
              {l.sorumlu_adi ? ` · ${l.sorumlu_adi}` : ''}
            </li>
          ))}
        </ul>
      )} />
  );
}

/** S2: görüşmeden ön dolu lokasyon formu; kullanıcı eksikleri tamamlar. */
function LokasyonaDonustur({ api, secenek, erisim, g, onKapat, onTamam }) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const genis = Grid.useBreakpoint().md;
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const sebep = Form.useWatch('sebep', form);

  useEffect(() => {
    const not = [g.gorusme_notu, `Satış Süreci görüşmesi #${g.id} (${tarih(g.gorusme_tarihi)}, ${g.gorusen_adi || ''})`]
      .filter(Boolean).join('\n');
    form.setFieldsValue({
      Name: g.lokasyon_adi, kategori: g.kategori, sebep: g.olusturma_sebebi, proje: g.proje, firma: 0, segment: 0,
      ATM_Kabin_Adedi: g.kabin_sayisi, Maksimum_Olabilecek_Kabin_Adedi: g.kabin_sayisi, yer_sahibi_ad: g.ys_ad,
      yer_sahibi_tel: g.ys_tel, kira_tutar: g.kira_tutar, odeme_sekli: g.odeme_sekli, kdv_stopaj: g.kdv_stopaj,
      genelaciklama: not, Enlem: g.Enlem, Boylam: g.Boylam, ProvinceID: g.ProvinceID, DistrictID: g.DistrictID,
      Adres: g.adres, sorumlu: g.gorusen,
    });
  }, [g, form]);

  const gonder = async () => {
    const v = await form.validateFields();
    setGonderiliyor(true);
    try {
      const s = await api.post(`/gorusme/${g.id}/lokasyona-donustur`, bosToNull(v));
      onTamam(s);
    } catch (e) {
      message.error(hata(e));
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <Modal open title={`Lokasyona dönüştür — görüşme #${g.id}`} onCancel={onKapat} onOk={gonder} confirmLoading={gonderiliyor}
      okText="Aday lokasyon aç" cancelText="Vazgeç" width={genis ? 820 : '100%'}
      style={genis ? undefined : { top: 0, maxWidth: '100vw', padding: 0 }}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Lokasyon Aday (durum 1) olarak açılır; sorumlusu görüşmeyi yapan kişidir."
        description={g.fotograflar?.length ? `${g.fotograflar.length} fotoğraf lokasyonun resimlerine kopyalanır.` : 'Görüşmede fotoğraf yok.'} />
      <YakinLokasyonlar api={api} g={g} />
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item name="Name" label="Lokasyon adı" rules={[{ required: true, min: 2, max: 120, whitespace: true }]}>
          <Input maxLength={120} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="kategori" label="Kategori" rules={[{ required: true, message: 'Kategori seçin' }]}>
              <Select {...aramali} options={secenek.kategoriler} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="proje" label="Proje">
              <Select {...aramali} options={secenek.projeler} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item name="sebep" label="Oluşturulma sebebi" rules={[{ required: true }]}>
              <Select options={secenek.sebepler} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="firma" label="Firma" rules={[{ required: true }]}>
              <Select options={secenek.firmalar} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="segment" label="Segment" rules={[{ required: true }]}>
              <Select options={secenek.segmentler} />
            </Form.Item>
          </Col>
        </Row>
        {sebep === 2 && (
          <Form.Item name="talep_eden_banka" label="Talep eden banka">
            <Select {...aramali} options={secenek.bankalar} />
          </Form.Item>
        )}
        <Row gutter={12}>
          <Col xs={12} sm={6}>
            <Form.Item name="ATM_Kabin_Adedi" label="Kabin sayısı">
              <InputNumber min={0} max={50} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Item name="Maksimum_Olabilecek_Kabin_Adedi" label="Maks. kabin">
              <InputNumber min={0} max={50} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="sorumlu" label="Sorumlu personel" rules={[{ required: true }]}
              tooltip={erisim.yonetici ? null : 'Sorumluyu başka birine vermek Satış Süreci yazma yetkisi ister.'}>
              <Select {...aramali} allowClear={false} options={secenek.kullanicilar} disabled={!erisim.yonetici} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="yer_sahibi_ad" label="Yer sahibi"><Input maxLength={250} /></Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="yer_sahibi_tel" label="Yer sahibi telefonu"><Input maxLength={250} /></Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item name="kira_tutar" label="Kira tutarı (₺)"><InputNumber {...paraGirdisi} max={1e9} /></Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item name="odeme_sekli" label="Kira ödeme şekli"><Select allowClear options={secenek.odeme_sekilleri} /></Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name="kdv_stopaj" label="Vergi"><Select allowClear options={secenek.vergi} /></Form.Item>
          </Col>
        </Row>
        <Form.Item name="aciklama_kira" label="Kiralama açıklaması (kira, stopaj, ödeme, sözleşme)">
          <Input.TextArea rows={2} maxLength={5000} />
        </Form.Item>
        <Form.Item name="genelaciklama" label="Açıklama (diğer bilgiler)">
          <Input.TextArea rows={3} maxLength={10000} />
        </Form.Item>
        <HaritaliAdres form={form} api={api} iller={secenek.iller} adresAlan="Adres" mahalleAlan="Neighborhood"
          dogrulukAlani={null} />
      </Form>
    </Modal>
  );
}

export default function Gorusmeler({ api, erisim, secenek }) {
  const { message } = App.useApp();
  const ekran = Grid.useBreakpoint();
  const mobil = !ekran.md;
  const [aralik, setAralik] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [gorusen, setGorusen] = useState(null);
  const [durum, setDurum] = useState(null);
  const [proje, setProje] = useState(null);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [form, setForm] = useState(null); // {kayit} | null
  const [donustur, setDonustur] = useState(null);
  const [sonuc, setSonuc] = useState(null);

  const params = useMemo(() => ({
    bas: aralik[0].format('YYYY-MM-DD'), bit: aralik[1].format('YYYY-MM-DD'),
    gorusen: gorusen || undefined, durum: durum || undefined, proje: proje || undefined,
  }), [aralik, gorusen, durum, proje]);

  const yukle = useCallback(() => {
    setYukleniyor(true);
    api.get('/gorusme', params).then(setVeri).catch((e) => message.error(hata(e))).finally(() => setYukleniyor(false));
  }, [api, params, message]);
  useEffect(() => { yukle(); }, [yukle]);

  const guncelle = (g) => setVeri((v) => (v ? { ...v, satirlar: v.satirlar.some((x) => x.id === g.id)
    ? v.satirlar.map((x) => (x.id === g.id ? g : x)) : [g, ...v.satirlar] } : v));

  const sahip = (g) => erisim.yonetici || [g.gorusen, g.ekleyen].includes(erisim.kullanici_no);
  const durumDegistir = (g, d) => api.post(`/gorusme/${g.id}/durum`, { durum: d })
    .then((y) => { guncelle(y); message.success(d === 'olumsuz' ? 'Olumsuz olarak işaretlendi.' : 'Görüşme yeniden açıldı.'); })
    .catch((e) => message.error(hata(e)));
  const sil = (g) => api.del(`/gorusme/${g.id}`)
    .then(() => { setVeri((v) => ({ ...v, satirlar: v.satirlar.filter((x) => x.id !== g.id) })); message.success('Görüşme silindi.'); })
    .catch((e) => message.error(hata(e)));

  const islemler = (g) => {
    const yaz = erisim.yazma && sahip(g);
    const acik = g.durum === 'acik';
    return (
      <Space size={2} wrap>
        <Tooltip title={yaz && g.durum !== 'donusturuldu' ? 'Düzenle' : 'Görüntüle'}>
          <Button size="small" type="text" icon={yaz && g.durum !== 'donusturuldu' ? <EditOutlined /> : <EyeOutlined />}
            onClick={() => setForm({ kayit: g })} aria-label="Düzenle" />
        </Tooltip>
        {yaz && acik && (
          <Button size="small" type="primary" ghost icon={<HomeOutlined />} onClick={() => setDonustur(g)}>
            {mobil ? 'Lokasyona dönüştür' : 'Dönüştür'}
          </Button>
        )}
        {yaz && acik && (
          <Popconfirm title="Görüşme olumsuz olarak işaretlensin mi?" okText="Olumsuz" cancelText="Vazgeç"
            onConfirm={() => durumDegistir(g, 'olumsuz')}>
            <Tooltip title="Olumsuz"><Button size="small" type="text" icon={<StopOutlined />} aria-label="Olumsuz" /></Tooltip>
          </Popconfirm>
        )}
        {yaz && g.durum === 'olumsuz' && (
          <Tooltip title="Yeniden aç">
            <Button size="small" type="text" icon={<UndoOutlined />} onClick={() => durumDegistir(g, 'acik')} aria-label="Yeniden aç" />
          </Tooltip>
        )}
        {yaz && g.durum !== 'donusturuldu' && (
          <Popconfirm title="Görüşme silinsin mi?" okText="Sil" okButtonProps={{ danger: true }} cancelText="Vazgeç" onConfirm={() => sil(g)}>
            <Tooltip title="Sil"><Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Sil" /></Tooltip>
          </Popconfirm>
        )}
      </Space>
    );
  };

  const satirlar = useMemo(() => (veri?.satirlar || []).map((g) => ({
    ...g, il_ilce: [g.il_ad, g.ilce_adi].filter(Boolean).join(' / '), foto_sayisi: g.fotograflar.length,
  })), [veri]);

  const kolonlar = useMemo(() => enhanceColumns([
    { title: 'Tarih', dataIndex: 'gorusme_tarihi', width: 100, render: tarih },
    { title: 'Görüşen', dataIndex: 'gorusen_adi', width: 150 },
    { title: 'Lokasyon adı', dataIndex: 'lokasyon_adi', width: 240 },
    { title: 'Kategori', dataIndex: 'kategori_adi', width: 150 },
    { title: 'Proje', dataIndex: 'proje_adi', width: 170 },
    { title: 'İl / İlçe', dataIndex: 'il_ilce', width: 150 },
    { title: 'Kabin', dataIndex: 'kabin_sayisi', width: 75, align: 'right' },
    { title: 'Kira', dataIndex: 'kira_tutar', width: 120, align: 'right', render: (v) => <span className="nf-amount">{para(v)}</span> },
    { title: 'Foto', dataIndex: 'foto_sayisi', width: 130, noFilter: true, render: (n, g) => (n ? (
      <Space size={4}><FotoSeridi g={{ ...g, fotograflar: g.fotograflar.slice(0, 2) }} boyut={36} />
        {n > 2 ? <Text type="secondary">+{n - 2}</Text> : null}</Space>) : null) },
    { title: 'Durum', dataIndex: 'durum_adi', width: 190, render: (v, g) => (
      <Space size={4} wrap><DurumEtiketi durum={g.durum} ad={v} />{g.lokasyon ? <LokasyonLinki id={g.lokasyon} /> : null}</Space>) },
    { title: '', key: 'islem', width: 190, fixed: 'right', render: (_, g) => islemler(g) },
  ]), [erisim, mobil]); // eslint-disable-line react-hooks/exhaustive-deps

  const ozet = useMemo(() => ({
    toplam: satirlar.length, acik: satirlar.filter((g) => g.durum === 'acik').length,
    donusen: satirlar.filter((g) => g.durum === 'donusturuldu').length, olumsuz: satirlar.filter((g) => g.durum === 'olumsuz').length,
  }), [satirlar]);

  const disaAktar = () => excelIndir(api, { bolum: 'gorusme', ...params }, `satis-gorusmeler-${params.bas}-${params.bit}.xlsx`)
    .catch((e) => message.error(hata(e)));

  return (
    <div>
      <Space wrap style={{ marginBottom: 12, width: '100%' }}>
        <RangePicker value={aralik} format="DD.MM.YYYY" allowClear={false} inputReadOnly
          onChange={(v) => v && setAralik(v)} style={{ width: mobil ? '100%' : 250 }}
          presets={[
            { label: 'Bu hafta', value: [pazartesi(dayjs()), dayjs()] },
            { label: 'Geçen hafta', value: [pazartesi(dayjs()).subtract(7, 'day'), pazartesi(dayjs()).subtract(1, 'day')] },
            { label: 'Son 30 gün', value: [dayjs().subtract(30, 'day'), dayjs()] },
            { label: 'Bu ay', value: [dayjs().startOf('month'), dayjs()] },
          ]} />
        <Select {...aramali} placeholder="Görüşen" value={gorusen} onChange={setGorusen} options={secenek.kullanicilar} style={{ width: 180 }} />
        <Select allowClear placeholder="Durum" value={durum} onChange={setDurum} options={secenek.gorusme_durumlari} style={{ width: 160 }} />
        <Select {...aramali} placeholder="Proje" value={proje} onChange={setProje} options={secenek.projeler} style={{ width: 200 }} />
        <Button icon={<ReloadOutlined />} onClick={yukle} loading={yukleniyor}>Yenile</Button>
        <Button icon={<FileExcelOutlined />} onClick={disaAktar}>Excel</Button>
        {erisim.yazma && <Button type="primary" icon={<PlusOutlined />} onClick={() => setForm({ kayit: null })}>Yeni görüşme</Button>}
      </Space>
      <Space wrap style={{ marginBottom: 8 }}>
        <Tag>{ozet.toplam} görüşme</Tag>
        <Tag color="processing">{ozet.acik} açık</Tag>
        <Tag color="success">{ozet.donusen} lokasyona dönüştü</Tag>
        <Tag>{ozet.olumsuz} olumsuz</Tag>
        {!erisim.yazma && <Text type="secondary">Salt okunur: kayıt girmek için keşif ekibinde olmanız gerekir.</Text>}
      </Space>

      {mobil ? (
        <List loading={yukleniyor} dataSource={satirlar} locale={{ emptyText: <Empty description="Bu aralıkta görüşme yok" /> }}
          renderItem={(g) => (
            <List.Item style={{ padding: '6px 0' }}>
              <Card size="small" style={{ width: '100%' }}
                title={<Space direction="vertical" size={0}><Text strong>{g.lokasyon_adi}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{tarih(g.gorusme_tarihi)} · {g.gorusen_adi}</Text></Space>}
                extra={<DurumEtiketi durum={g.durum} ad={g.durum_adi} />}>
                <Descriptions size="small" column={1} colon={false} items={[
                  g.kategori_adi && { key: 'k', label: 'Kategori', children: g.kategori_adi },
                  g.il_ilce && { key: 'i', label: 'Yer', children: g.il_ilce },
                  g.kira_tutar != null && { key: 'r', label: 'Kira', children: para(g.kira_tutar) },
                  g.lokasyon && { key: 'l', label: 'Lokasyon', children: <LokasyonLinki id={g.lokasyon} /> },
                ].filter(Boolean)} />
                <div style={{ margin: '6px 0' }}><FotoSeridi g={g} boyut={52} /></div>
                {islemler(g)}
              </Card>
            </List.Item>
          )} />
      ) : (
        <Table rowKey="id" size="small" loading={yukleniyor} columns={kolonlar} dataSource={satirlar}
          scroll={{ x: toplamGenislik(kolonlar) }} pagination={{ pageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: <Empty description="Bu aralıkta görüşme yok" /> }} />
      )}

      {form && (
        <GorusmeFormu api={api} secenek={secenek} erisim={erisim} kayit={form.kayit} onKapat={() => setForm(null)}
          onKaydedildi={(g) => guncelle(g)} />
      )}
      {donustur && (
        <LokasyonaDonustur api={api} secenek={secenek} erisim={erisim} g={donustur} onKapat={() => setDonustur(null)}
          onTamam={(s) => { setDonustur(null); guncelle(s.gorusme); setSonuc(s); }} />
      )}
      {sonuc && (
        <Modal open footer={null} onCancel={() => setSonuc(null)} width={520}>
          <Result status="success" title={`Aday lokasyon açıldı: ${sonuc.MaptriksID}`}
            subTitle={sonuc.foto.kopyalanan.length ? `${sonuc.foto.kopyalanan.length} fotoğraf lokasyona kopyalandı.` : null}
            extra={[
              <Link key="detay" to={sonuc.link}><Button type="primary">Lokasyon detayına git</Button></Link>,
              <Button key="kapat" onClick={() => setSonuc(null)}>Kapat</Button>,
            ]}>
            {sonuc.foto.hatalar.length > 0 && (
              <Alert type="warning" showIcon message="Bazı fotoğraflar kopyalanamadı (lokasyon yine de açıldı)"
                description={<ul style={{ margin: 0, paddingLeft: 16 }}>{sonuc.foto.hatalar.map((h) => <li key={h}>{h}</li>)}</ul>} />
            )}
          </Result>
        </Modal>
      )}
    </div>
  );
}

